import { useTranslation } from "react-i18next";
import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { BarcodeScanner } from '@/shared/components/scanner/BarcodeScanner';
import { cn } from '@/shared/utils';
import { CheckCircle2, Circle, AlertTriangle, ArrowRight, X, RotateCcw, Package, MapPin, FileText, Truck } from 'lucide-react';

// Step status icons
const stepIcons = {
  document: FileText,
  item: Package,
  location: MapPin,
  vehicle: Truck
};

// Step indicator component
const StepIndicator = ({
  steps,
  currentStep,
  completedSteps
}) => <div className="flex items-center gap-2 mb-6 p-3 bg-muted/30 rounded-lg">
    {steps.map((step, idx) => {
    const isCompleted = completedSteps.includes(idx);
    const isCurrent = idx === currentStep;
    const Icon = stepIcons[step.type] || Circle;
    return <React.Fragment key={idx}>
          <div className={cn("flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all", isCompleted && "bg-emerald-100 text-emerald-700", isCurrent && !isCompleted && "bg-blue-100 text-blue-700 ring-2 ring-blue-300", !isCompleted && !isCurrent && "bg-gray-100 text-gray-400")}>
            {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
            <span className="font-medium">{step.label}</span>
          </div>
          {idx < steps.length - 1 && <ArrowRight className={cn("h-4 w-4", isCompleted ? "text-emerald-500" : "text-gray-300")} />}
        </React.Fragment>;
  })}
  </div>;

// Scanned items list
const ScannedItemsList = ({
  items,
  expectedItems,
  onManualSelect
}) => {
  const { t } = useTranslation("common");
  return <div className="border rounded-lg overflow-hidden mt-4">
    <div className="bg-muted/30 px-3 py-2 border-b">
      <span className="text-sm font-medium">{t("scannedItems", "Scanned Items (")}
      {items.length}/{expectedItems.length})
      </span>
      <span className="text-[10px] text-muted-foreground ml-2 uppercase tracking-tighter">{t("clickRowTo", "(Click row to select manually)")}

    </span>
    </div>
    <div className="max-h-[200px] overflow-y-auto">
      {expectedItems.map((expected, idx) => {

        const scanned = items.find(i => i.sku === expected.sku || i.gtin === expected.gtin || i.fields?.GTIN?.parsed === expected.gtin);
        return <div key={idx} onClick={() => !scanned && onManualSelect?.(expected)} className={cn("flex items-center justify-between px-3 py-2 border-b last:border-0 transition-colors", scanned ? "bg-emerald-50/50" : "bg-white hover:bg-blue-50 cursor-pointer")} data-testid={`sku-row-${expected.sku}`}>
          
            <div className="flex items-center gap-2">
              {scanned ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-gray-300" />}
              <div>
                <span className="font-mono text-sm">{expected.sku}</span>
                <span className="text-xs text-muted-foreground ml-2">{t("qty", "Qty:")}
                {expected.quantity}
                </span>
              </div>
            </div>
            {scanned && scanned.lot && <Badge variant="outline" className="text-xs">{t("lot", "Lot:")}
            {scanned.lot}
              </Badge>}
          </div>;
      })}
    </div>
  </div>;
};

// Main workflow dialog component
export const ScanWorkflowDialog = ({
  open,
  onOpenChange,
  title,
  icon: TitleIcon = Package,
  steps,
  expectedItems = [],
  onComplete,
  onStepComplete,
  validationMode = 'flexible' // 'strict' or 'flexible'
}) => {
  const {
    t
  } = useTranslation("wms");
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [scannedItems, setScannedItems] = useState([]);
  const [scanResults, setScanResults] = useState([]);
  const [lastScanWarning, setLastScanWarning] = useState(null);
  const currentStepConfig = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const allStepsCompleted = completedSteps.length === steps.length;

  // Complete current step and advance
  const completeStep = useCallback(() => {
    setCompletedSteps(prev => {
      if (prev.includes(currentStep)) return prev;
      return [...prev, currentStep];
    });
    if (!isLastStep) {
      setCurrentStep(prev => prev + 1);
      setLastScanWarning(null);
      console.log(`[ScanWorkflow] Advancing to step ${currentStep + 1}`);
    }
  }, [currentStep, isLastStep]);
  useEffect(() => {
    const stepConfig = steps[currentStep];
    if (stepConfig && stepConfig.type === 'item' && !completedSteps.includes(currentStep)) {
      const allScanned = expectedItems.every(expected => {
        const match = scannedItems.some(s => {
          const sGtin = String(s.gtin || s.fields?.GTIN?.parsed || s.fields?.GTIN?.raw || "").trim();
          const eGtin = String(expected.gtin || "").trim();
          const sSku = String(s.sku || "").trim();
          const eSku = String(expected.sku || "").trim();
          return sGtin && sGtin === eGtin || sSku && sSku === eSku || sSku.includes(eSku);
        });
        return match;
      });
      console.log(`[ScanWorkflow] Step ${currentStep} (${stepConfig.label}) progress: ${scannedItems.length}/${expectedItems.length}, AllMatched: ${allScanned}`);
      if (!allScanned) {
        console.log("[ScanWorkflow] Expected GTINs:", expectedItems.map(e => e.gtin));
        console.log("[ScanWorkflow] Scanned GTINs:", scannedItems.map(s => s.gtin));
      }
      if (stepConfig.requiredCount && scannedItems.length >= stepConfig.requiredCount) {
        console.log("[ScanWorkflow] Auto-completing step based on count");
        completeStep();
      } else if (stepConfig.matchItems && expectedItems.length > 0 && allScanned) {
        console.log("[ScanWorkflow] Auto-completing step based on matches");
        completeStep();
      }
    }
  }, [scannedItems, currentStep, steps, expectedItems, completeStep, completedSteps]);

  // Handle scan result
  const handleScan = useCallback(result => {
    try {
      console.log("[ScanWorkflow] handleScan triggered for:", result.raw);
      setLastScanWarning(null);
      if (!result.valid) {
        setLastScanWarning({
          type: 'error',
          message: 'Invalid barcode'
        });
        return;
      }
      const stepConfig = steps[currentStep];
      if (!stepConfig) return;
      const expectedTypes = stepConfig.expectedTypes || [];
      if (expectedTypes.length > 0 && !expectedTypes.includes(result.type)) {
        setLastScanWarning({
          type: validationMode === 'strict' ? 'error' : 'warning',
          message: `Expected ${expectedTypes.join('/')}, got ${result.type}`
        });
        if (validationMode === 'strict') return;
      }
      if (stepConfig.type === 'item') {
        const gtin = String(result.fields?.GTIN?.parsed || result.fields?.GTIN?.raw || "").trim();
        const sku = String(result.raw || "").trim();
        const lot = String(result.fields?.BATCH_LOT?.parsed || result.fields?.BATCH_LOT?.raw || "").trim();
        setScannedItems(prev => {
          const newItem = {
            gtin,
            lot,
            sku,
            fields: result.fields,
            scannedAt: new Date().toISOString()
          };
          console.log("[ScanWorkflow] Adding item to scan list:", newItem.gtin || newItem.sku);
          return [...prev, newItem];
        });
      } else {
        completeStep();
      }
      onStepComplete?.(currentStep, result);
    } catch (err) {
      console.error("[ScanWorkflow] Fatal error in handleScan:", err);
    }
  }, [currentStep, steps, validationMode, onStepComplete, completeStep]);

  // Reset workflow
  const handleReset = () => {
    setCurrentStep(0);
    setCompletedSteps([]);
    setScannedItems([]);
    setScanResults([]);
    setLastScanWarning(null);
  };

  // Complete workflow
  const handleComplete = () => {
    onComplete?.({
      scannedItems,
      scanResults,
      completedAt: new Date().toISOString()
    });
    onOpenChange(false);
    handleReset();
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="scan-workflow-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TitleIcon className="h-5 w-5" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step Progress */}
          <StepIndicator steps={steps} currentStep={currentStep} completedSteps={completedSteps} />
          

          {/* Current Step Instructions */}
          {!allStepsCompleted && currentStepConfig && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-1">{t("step", "Step")}
              {currentStep + 1}: {currentStepConfig.label}
              </h4>
              <p className="text-sm text-blue-600">
                {currentStepConfig.instruction}
              </p>
            </div>}

          {/* Warning/Error Display */}
          {lastScanWarning && <div className={cn("flex items-center gap-2 p-3 rounded-lg", lastScanWarning.type === 'error' ? "bg-red-50 border border-red-200 text-red-700" : "bg-amber-50 border border-amber-200 text-amber-700")}>
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{lastScanWarning.message}</span>
            </div>}

          {/* Scanner */}
          {!allStepsCompleted && <BarcodeScanner onScan={handleScan} scanMode={currentStepConfig?.scanMode || 'any'} placeholder={currentStepConfig?.placeholder || 'Scan barcode...'} showSamples />}

          {/* Scanned Items (for item steps) */}
          {currentStepConfig?.type === 'item' && expectedItems.length > 0 && <ScannedItemsList items={scannedItems} expectedItems={expectedItems} onManualSelect={item => {
          console.log("[ScanWorkflow] Manual selection for SKU:", item.sku);
          handleScan({
            valid: true,
            type: 'MANUAL_SELECT',
            raw: item.sku,
            fields: {
              GTIN: {
                parsed: item.gtin,
                raw: item.gtin
              },
              BATCH_LOT: {
                parsed: item.lot,
                raw: item.lot
              }
            }
          });
        }} />}

          {/* Completion State */}
          {allStepsCompleted && <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-emerald-800">{t("allStepsCompleted", "All Steps Completed!")}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {scannedItems.length}{t("itemsScannedSuccessfully", "item(s) scanned successfully")}
            </p>
            </div>}

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={handleReset} className="gap-2">
              <RotateCcw className="h-4 w-4" />{t("reset", "Reset")}

            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                <X className="h-4 w-4 mr-1" />{t("cancel", "Cancel")}

              </Button>
              {allStepsCompleted && <Button onClick={handleComplete} className="bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle2 className="h-4 w-4 mr-1" />{t("complete", "Complete")}

              </Button>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
};
export default ScanWorkflowDialog;